/**
 * Connection Validator
 * Validates Supabase database connections
 */

import { SupabaseClient } from '@supabase/supabase-js';
import { RETRY_CONFIG } from '@/services/supabase/config';

export interface ValidationResult {
  success: boolean;
  error?: string;
}

/**
 * Validates the connection to a Supabase database
 * Attempts to access the profiles table to verify the connection
 *
 * @param client - Supabase client to validate
 * @param maxAttempts - Maximum number of attempts (default: 3)
 * @returns Promise<boolean> - true if the connection is valid
 */
export const validateConnection = async (
  client: SupabaseClient,
  maxAttempts: number = RETRY_CONFIG.maxAttempts
): Promise<boolean> => {
  let attempts = 0;

  while (attempts < maxAttempts) {
    try {
      attempts++;
      const { error } = await client.from('profiles').select('id').limit(1);

      // If there is no error or the table does not exist (42P01), the connection is valid
      if (!error || error.code === '42P01') {
        console.log(`✅ Client validation successful`);
        return true;
      }

      if (attempts < maxAttempts) {
        await delay(RETRY_CONFIG.initialDelay);
      } else {
        throw new Error(`Invalid database connection: ${error.message}`);
      }
    } catch (error) {
      console.error(`⚠️ Validation attempt ${attempts} failed:`, error);

      if (attempts < maxAttempts) {
        await delay(RETRY_CONFIG.initialDelay);
      } else {
        throw new Error('Unable to connect to database', { cause: error });
      }
    }
  }

  return false;
};

/**
 * Delay helper
 */

const delay = (ms: number): Promise<void> => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};
